import { PageHero } from "@/components/PageHero";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal, SectionHeading } from "@/components/motion";
import { CtaBand } from "@/components/CtaBand";
import { LeafMark } from "@/components/LeafMark";
import { usePageMeta } from "@/lib/seo";
import { IMG } from "@/lib/site";

export const WhyKerala = () => {
    const {  t, tHtml, site: siteData } = useLang();
    const { WHY_KERALA_POINTS } = siteData;
    usePageMeta(
        t("meta.why_kerala.title", "Why Kerala — Where Healthcare Meets Wellbeing"),
        t("meta.why_kerala.desc", "Kerala, God's Own Country, combines modern healthcare, Ayurveda and wellness traditions, nature and warm hospitality — UniversHeal's initial destination in India, with expansion across India planned.")
    );
    return (
        <>
            <PageHero
                eyebrow={t("why_kerala.eyebrow", "Why Kerala — God's Own Country")}
                title={
                    <>
                        Kerala — where healthcare{" "}
                        <span className="italic text-pine">meets wellbeing.</span>
                    </>
                }
                intro={t("why_kerala.intro", "Kerala offers a distinctive combination: modern healthcare, a living Ayurveda tradition, wellness culture, restorative nature, warm hospitality and a rich cultural life. It is UniversHeal's initial destination — with plans to expand across India.")}
            />

            <section className="pb-8">
                <div className="container-site">
                    <Reveal>
                        <div className="rounded-2xl overflow-hidden h-72 sm:h-[480px] shadow-[0_40px_90px_rgba(6,60,53,0.16)] relative">
                            <img
                                src={IMG.keralaMist}
                                alt="Mist-covered mountains of the Western Ghats rising over a deep green Kerala valley"
                                data-testid="kerala-hero-image"
                                className="w-full h-full object-cover"
                            />
                            <div
                                aria-hidden="true"
                                className="absolute inset-0 bg-gradient-to-t from-forest/50 via-transparent to-transparent"
                            />
                            <p
                                className="absolute bottom-6 left-6 sm:bottom-8 sm:left-8 font-serif italic text-2xl sm:text-3xl text-ivory drop-shadow-[0_2px_10px_rgba(6,60,53,0.8)] max-w-lg"
                                data-testid="kerala-editorial-statement"
                            >
                                {t('why_kerala.statement', '"Where advanced medicine meets timeless beauty."')}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="section-pad pt-10">
                <div className="container-site">
                    <SectionHeading
                        index="—"
                        eyebrow={t("why_kerala.eyebrow", "What Kerala Offers")}
                        title={t("why_kerala.title1", "What Kerala offers international patients.")}
                        intro={t("why_kerala.intro", "Six reasons patients from around the world choose Kerala for their care — without overstating any of them.")}
                    />
                    <div className="mt-12 max-w-4xl">
                        {WHY_KERALA_POINTS.map((point, i) => (
                            <Reveal key={point.title} delay={i * 0.05}>
                                <div
                                    data-testid={`kerala-point-${i + 1}`}
                                    className="border-t border-pine/15 py-8 grid grid-cols-[2.5rem_1fr] gap-4 sm:gap-10"
                                >
                                    <span className="font-serif italic text-2xl text-natural leading-none pt-1">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <div>
                                        <h3 className="font-serif font-medium text-2xl sm:text-3xl text-forest leading-snug">
                                            {point.title}
                                        </h3>
                                        <p className="mt-3 text-sm sm:text-[15px] leading-relaxed text-moss max-w-2xl">
                                            {point.desc}
                                        </p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* modern medical infrastructure */}
            <section className="section-pad pt-0">
                <div className="container-site">
                    <SectionHeading
                        index="—"
                        eyebrow={t("why_kerala.eyebrow", "Modern Infrastructure")}
                        title={
                            <>
                                Serious medicine,{" "}
                                <span className="italic text-pine">quietly advanced.</span>
                            </>
                        }
                        intro={t("why_kerala.intro", "Behind Kerala's green calm stands contemporary medical infrastructure — modern hospitals, advanced diagnostics and operating environments of international standard.")}
                    />
                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            {
                                img: IMG.hospitalOT,
                                alt: "A modern operating theatre with advanced surgical lights and robotic equipment in an Indian hospital",
                                cap: "Advanced operating theatres",
                                id: "infra-ot",
                            },
                            {
                                img: IMG.hospitalImaging,
                                alt: "An MRI scanner suite with a radiology technologist reviewing scans in a modern Indian hospital",
                                cap: "Advanced imaging & diagnostics",
                                id: "infra-imaging",
                            },
                            {
                                img: IMG.hospitalLab,
                                alt: "Indian laboratory scientists working in a modern hospital diagnostic laboratory",
                                cap: "High-tech laboratories",
                                id: "infra-lab",
                            },
                        ].map((item, i) => (
                            <Reveal key={item.id} delay={i * 0.08} className="h-full">
                                <figure
                                    data-testid={`kerala-${item.id}`}
                                    className="h-full bg-white border border-pine/10 rounded-2xl overflow-hidden card-lift"
                                >
                                    <div className="h-52 overflow-hidden">
                                        <img
                                            src={item.img}
                                            alt={item.alt}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                    <figcaption className="p-6 font-serif italic text-xl text-forest">
                                        {item.cap}
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.1}>
                        <p className="mt-8 text-xs leading-relaxed text-moss max-w-2xl">
                            {t("why_kerala.text4", "Illustrative imagery of contemporary medical environments — shown to represent the standard of infrastructure, not to depict any specific hospital or clinician.")}
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="section-pad pt-0">
                <div className="container-site grid lg:grid-cols-2 gap-6 lg:gap-8">
                    <Reveal>
                        <div className="rounded-2xl overflow-hidden h-72 sm:h-[420px] shadow-[0_30px_70px_rgba(6,60,53,0.14)]">
                            <img
                                src={IMG.keralaBackwaters}
                                alt="Perfectly still Kerala backwaters reflecting coconut palms at golden hour"
                                data-testid="kerala-backwaters-image"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="rounded-2xl overflow-hidden h-72 sm:h-[420px] shadow-[0_30px_70px_rgba(6,60,53,0.14)]">
                            <img
                                src={IMG.keralaCoast}
                                alt="Golden sunset over dramatic coastal cliffs and the Arabian Sea in Kerala"
                                data-testid="kerala-coast-image"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </Reveal>
                </div>
                <div className="container-site mt-14">
                    <SectionHeading
                        index="—"
                        eyebrow={t("why_kerala.eyebrow", "A Restorative Setting")}
                        title={t("why_kerala.title2", "A place where rest comes naturally.")}
                        intro={t("why_kerala.intro", "Recovery asks for quiet — green surroundings, gentle weather, unhurried days. Kerala's calm is not a backdrop to treatment; it is a restorative setting alongside professional healthcare, for patients and the family who accompany them.")}
                    />
                    <Reveal delay={0.1}>
                        <p className="mt-8 text-xs leading-relaxed text-moss max-w-2xl">
                            {t("why_kerala.text5", "Kerala's landscapes, hospitality and culture make stays comfortable and restful. They complement — never replace — the medical care provided by qualified professionals.")}
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="section-pad pt-0">
                <div className="container-site">
                    <SectionHeading
                        index="—"
                        eyebrow={t("why_kerala.eyebrow", "More Than Medical Travel")}
                        title={
                            <>
                                Healthcare, wellness and Ayurveda —{" "}
                                <span className="italic text-pine">side by side.</span>
                            </>
                        }
                        intro={t("why_kerala.intro", "Kerala's combination is hard to find anywhere else: modern hospitals and experienced clinicians alongside a living Ayurveda tradition, wellness culture and restorative natural beauty. The green calm of the state complements professional care — for patients recovering, for wellness guests, and for those seeking traditional Ayurvedic experiences.")}
                    />
                    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {[
                            { title: "Modern healthcare", desc: "Advanced hospitals, diagnostics and internationally experienced clinical teams.", id: "combo-medical" },
                            { title: "Ayurveda", desc: "A longstanding tradition — Kerala is an important home of Ayurvedic practice and education.", id: "combo-ayurveda" },
                            { title: "Wellness", desc: "Yoga, nutrition and restorative retreat culture, lived here as daily life.", id: "combo-wellness" },
                            { title: "Nature", desc: "Highlands, backwaters and coastline — a naturally restorative setting.", id: "combo-nature" },
                            { title: "Hospitality", desc: "A wide choice of premium stays and a culture of caring for guests.", id: "combo-hospitality" },
                            { title: "Culture", desc: "Heritage, cuisine and the rhythm of daily life in God's Own Country.", id: "combo-culture" },
                        ].map((item, i) => (
                            <Reveal key={item.id} delay={i * 0.06} className="h-full">
                                <div
                                    data-testid={`kerala-combination-${item.id}`}
                                    className="h-full bg-white border border-pine/10 rounded-2xl p-7 card-lift"
                                >
                                    <h3 className="font-serif font-medium text-xl text-forest">{item.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-moss">{item.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.1}>
                        <p className="mt-8 text-xs leading-relaxed text-moss max-w-2xl">
                            {t("why_kerala.text6", "Kerala's environment and traditions support comfort and rest — they complement, and never replace, the medical care provided by qualified professionals.")}
                            Kerala is UniversHeal's initial destination; care is
                            {t("why_kerala.text7", "coordinated elsewhere in India as availability is established.")}
                        </p>
                    </Reveal>
                </div>
            </section>

            <CtaBand
                headline={t("why_kerala.headline", "See it for yourself.")}
                text={t("why_kerala.text", "Tell us about your needs and we will help you understand what care in Kerala could look like for you.")}
            />
        </>
    );
};
